/**
 * The generative-UI dispatcher — one renderer per {@link UiDirective} `kind`
 * (OCP: a new surface is a new `<Match>` + component, no fork of existing ones).
 * The agent emits directives on the run channel; the chat stream hands each to
 * this component, which routes it to its typed renderer. Input directives
 * (`form`/`choice`/`file`) round-trip a value back to the waiting run via
 * `handlers.onSubmit` / `handlers.onFile`; display directives
 * (`card`/`gallery`/`modal`/`toast`) render read-only or open a surface.
 */
import type { UiDirective, UiSurface } from '@4f/shared';
import { type JSX, Match, Switch } from 'solid-js';
import { ChoiceRenderer } from './renderers/ChoiceRenderer.tsx';
import {
  CardRenderer,
  GalleryDirective,
  ModalDirective,
  ToastDirective,
} from './renderers/display.tsx';
import { FileRenderer } from './renderers/FileRenderer.tsx';
import { FormRenderer } from './renderers/FormRenderer.tsx';

/** Callbacks a renderer uses to round-trip an answer or open a surface. */
export interface GenerativeUiHandlers {
  /** Echo a value back to the parked run — form values, the chosen option, or a card action. */
  onSubmit(value: unknown): void;
  /** Hand a selected concept image to the media-ingest seam (the `file` directive). */
  onFile?(file: File): void;
  /** Open the shared Artifacts lightbox at a set of assets (the `gallery` directive). */
  onOpenGallery?(assetIds: readonly string[], startIndex: number): void;
  /**
   * Resolve an asset id to a fetchable URL (the `gallery` directive carries ids,
   * never URLs). `undefined` while the companion's gallery is still loading —
   * the tile shows its skeleton rather than a broken image.
   */
  assetUrl?(assetId: string): string | undefined;
  /** Open a modal surface — settings/builder/billing/referral (the `modal` directive). */
  onOpenSurface?(surface: UiSurface): void;
  /** Disable inputs once the directive is answered / the run is no longer waiting. */
  disabled?: boolean;
}

/** Narrow a directive to a specific `kind`, or `undefined` — feeds `<Match when>`. */
function ofKind<K extends UiDirective['kind']>(
  directive: UiDirective,
  kind: K,
): Extract<UiDirective, { kind: K }> | undefined {
  return directive.kind === kind ? (directive as Extract<UiDirective, { kind: K }>) : undefined;
}

export interface GenerativeUiProps {
  readonly directive: UiDirective;
  readonly handlers: GenerativeUiHandlers;
}

export function GenerativeUi(props: GenerativeUiProps): JSX.Element {
  return (
    <div class="f4-genui">
      <Switch>
        <Match when={ofKind(props.directive, 'form')}>
          {(directive) => <FormRenderer directive={directive()} handlers={props.handlers} />}
        </Match>
        <Match when={ofKind(props.directive, 'choice')}>
          {(directive) => <ChoiceRenderer directive={directive()} handlers={props.handlers} />}
        </Match>
        <Match when={ofKind(props.directive, 'file')}>
          {(directive) => <FileRenderer directive={directive()} handlers={props.handlers} />}
        </Match>
        <Match when={ofKind(props.directive, 'card')}>
          {(directive) => <CardRenderer directive={directive()} handlers={props.handlers} />}
        </Match>
        <Match when={ofKind(props.directive, 'gallery')}>
          {(directive) => <GalleryDirective directive={directive()} handlers={props.handlers} />}
        </Match>
        <Match when={ofKind(props.directive, 'modal')}>
          {(directive) => <ModalDirective directive={directive()} handlers={props.handlers} />}
        </Match>
        <Match when={ofKind(props.directive, 'toast')}>
          {(directive) => <ToastDirective directive={directive()} />}
        </Match>
      </Switch>
    </div>
  );
}
